import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Upload, X, ChevronDown } from 'lucide-react';
import { Make, Model } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

const CreateProductPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Form states
  const [makes, setMakes] = useState<Make[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedMake, setSelectedMake] = useState<number | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [serviceName, setServiceName] = useState('');
  const [actualPrice, setActualPrice] = useState('');
  const [markedPrice, setMarkedPrice] = useState('');
  const [serviceDuration, setServiceDuration] = useState('');
  const [serviceCost, setServiceCost] = useState('');
  const [marginCost, setMarginCost] = useState('');
  const [serviceImage, setServiceImage] = useState<File | null>(null);
  const [modelImage, setModelImage] = useState<File | null>(null);
  const [serviceImagePreview, setServiceImagePreview] = useState<string | null>(null);
  const [modelImagePreview, setModelImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Search state for autocomplete
  const [makeSearch, setMakeSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [showMakeDropdown, setShowMakeDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [filteredMakes, setFilteredMakes] = useState<Make[]>([]);
  const [filteredModels, setFilteredModels] = useState<Model[]>([]);

  useEffect(() => {
    loadMakes();
  }, []);

  // Filter makes based on search
  useEffect(() => {
    if (makeSearch) {
      const filtered = makes.filter(make => 
        make.name.toLowerCase().includes(makeSearch.toLowerCase())
      );
      setFilteredMakes(filtered);
    } else {
      setFilteredMakes(makes);
    }
  }, [makes, makeSearch]);

  // Filter models based on search
  useEffect(() => {
    if (modelSearch) {
      const filtered = models.filter(model => 
        model.name.toLowerCase().includes(modelSearch.toLowerCase())
      );
      setFilteredModels(filtered);
    } else {
      setFilteredModels(models);
    }
  }, [models, modelSearch]);

  const loadMakes = async () => {
    try {
      const response = await apiService.getMakes();
      if (response.isSuccess && response.entity) {
        setMakes(response.entity);
        setFilteredMakes(response.entity);
      }
    } catch (error) {
      console.error('Error loading makes:', error);
      toast.error('Failed to load makes');
    }
  };

  const loadModels = async (makeId: number) => {
    try {
      const response = await apiService.getModels(makeId);
      if (response.isSuccess && response.entity) {
        setModels(response.entity);
        setFilteredModels(response.entity);
      }
    } catch (error) {
      console.error('Error loading models:', error);
      toast.error('Error loading models');
    }
  };

  const handleMakeSelect = (make: Make) => {
    setSelectedMake(make.id);
    setMakeSearch(make.name);
    setShowMakeDropdown(false);
    // Clear model selection
    setSelectedModel(null);
    setModelSearch('');
    // Small delay to ensure state is updated before loading models
    setTimeout(() => {
      loadModels(make.id);
    }, 0);
  };

  const handleModelSelect = (model: Model) => {
    setSelectedModel(model);
    setModelSearch(model.name);
    setShowModelDropdown(false);
  };

  const handleCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      
      formData.append('makeId', selectedMake?.toString() || '');
      // Use selectedModel name if available and matches search, otherwise use the typed search text (for new models)
      const modelName = selectedModel?.name || modelSearch.trim();
      formData.append('modelName', modelName);
      formData.append('serviceName', serviceName);
      formData.append('actualPrice', actualPrice);
      formData.append('markedPrice', markedPrice);
      formData.append('serviceDuration', serviceDuration);
      formData.append('serviceCost', serviceCost);
      formData.append('MarginalCost', marginCost);
      
      if (serviceImage) {
        formData.append('serviceImage', serviceImage);
      }
      if (modelImage) {
        formData.append('modelImage', modelImage);
      }

      const response = await apiService.createProduct(formData);
      
      if (response.isSuccess) {
        toast.success('Product created successfully');
        navigate('/products');
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to create product');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Error creating product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    // Check if model is provided either by selection or by typing new name
    const hasModel = selectedModel || (modelSearch && modelSearch.trim());
    
    if (!selectedMake) {
      toast.error('Please select a Make');
      return false;
    }
    
    if (!hasModel) {
      toast.error('Please select or enter a Model name');
      return false;
    }
    
    if (!serviceName.trim()) {
      toast.error('Please enter a Service Name');
      return false;
    }
    
    if (!actualPrice) {
      toast.error('Please enter Parts Price');
      return false;
    }
    
    if (!markedPrice) {
      toast.error('Please enter Marked Price');
      return false;
    }
    
    if (!serviceDuration) {
      toast.error('Please enter Service Duration');
      return false;
    }
    
    if (!serviceCost) {
      toast.error('Please enter Service Cost');
      return false;
    }
    
    if (!marginCost) {
      toast.error('Please enter Margin Cost');
      return false;
    }
    
    if (parseFloat(actualPrice) <= 0 || parseFloat(markedPrice) <= 0 || parseInt(serviceDuration) <= 0 || parseFloat(serviceCost) <= 0 || parseFloat(marginCost) <= 0) {
      toast.error('Price, duration, and cost must be greater than 0');
      return false;
    }
    
    return true;
  };

  const handleImageUpload = (file: File, type: 'service' | 'model') => {
    if (file) {
      // Validate file type
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        toast.error('Please select a valid image file (PNG, JPEG, JPG)');
        return;
      }
      
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        if (type === 'service') {
          setServiceImage(file);
          setServiceImagePreview(e.target?.result as string);
        } else {
          setModelImage(file);
          setModelImagePreview(e.target?.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/products')}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Products
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Create New Product</h1>
            <p className="text-sm text-gray-500">Add a new service product to your catalog</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <form onSubmit={handleCreateProduct} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Make *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={makeSearch}
                    onChange={(e) => {
                      setMakeSearch(e.target.value);
                      setShowMakeDropdown(true);
                    }}
                    onFocus={() => setShowMakeDropdown(true)}
                    onClick={() => {
                      setShowMakeDropdown(true);
                      if (!makeSearch) {
                        setFilteredMakes(makes);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowMakeDropdown(false), 300)}
                    placeholder="Search and select make..."
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                  {showMakeDropdown && filteredMakes.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredMakes.map((make) => (
                        <button
                          key={make.id}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleMakeSelect(make);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-purple-50"
                        >
                          <div className="flex items-center space-x-3">
                            {make.imagePath && (
                              <img
                                src={apiService.getFullImageUrl(make.imagePath)}
                                alt={make.name}
                                className="w-6 h-6 rounded object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            )}
                            <span className="font-medium">{make.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={modelSearch}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setModelSearch(newValue);
                      setShowModelDropdown(true);
                      // Clear selectedModel if the typed text doesn't match the selected model
                      if (selectedModel && selectedModel.name !== newValue.trim()) {
                        setSelectedModel(null);
                      }
                    }}
                    onFocus={() => setShowModelDropdown(true)}
                    onClick={() => {
                      if (selectedMake) {
                        setShowModelDropdown(true);
                        if (!modelSearch) {
                          setFilteredModels(models);
                        }
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowModelDropdown(false), 300)}
                    placeholder="Search and select model or type new model name..."
                    disabled={!selectedMake}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className={`h-4 w-4 ${selectedMake ? 'text-gray-400' : 'text-gray-300'}`} />
                  </div>
                  {showModelDropdown && filteredModels.length > 0 && selectedMake && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredModels.map((model) => (
                        <button
                          key={model.id}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleModelSelect(model);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-purple-50"
                        >
                          <div className="flex items-center space-x-3">
                            {model.imagePath && (
                              <img
                                src={apiService.getFullImageUrl(model.imagePath)}
                                alt={model.name}
                                className="w-6 h-6 rounded object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            )}
                            <span className="font-medium">{model.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Name *</label>
                <input
                  type="text"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter service name"
                  required
                />
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Parts Price *</label>
                <input
                  type="number"
                  value={actualPrice}
                  onChange={(e) => setActualPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter parts price"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Marked Price *</label>
                <input
                  type="number"
                  value={markedPrice}
                  onChange={(e) => setMarkedPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter marked price"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Duration (days) *</label>
                <input
                  type="number"
                  value={serviceDuration}
                  onChange={(e) => setServiceDuration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter service duration"
                  min="1"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Cost *</label>
                <input
                  type="number"
                  value={serviceCost}
                  onChange={(e) => setServiceCost(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter service cost"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Margin Cost *</label>
                <input
                  type="number"
                  value={marginCost}
                  onChange={(e) => setMarginCost(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter margin cost"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>
          </div>

          {/* Image Uploads */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Images</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Image</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
                  {serviceImagePreview ? (
                    <div className="space-y-4">
                      <img src={serviceImagePreview} alt="Service preview" className="mx-auto h-32 w-32 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => {
                          setServiceImage(null);
                          setServiceImagePreview(null);
                        }}
                        className="inline-flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="text-sm text-gray-600">
                        <label className="cursor-pointer text-purple-600 hover:text-purple-700 font-medium">
                          <span>Upload a file</span>
                          <input
                            type="file"
                            className="sr-only"
                            accept="image/*"
                            onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0], 'service')}
                          />
                        </label>
                        <span> or drag and drop</span>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG up to 2MB</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model Image</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
                  {modelImagePreview ? (
                    <div className="space-y-4">
                      <img src={modelImagePreview} alt="Model preview" className="mx-auto h-32 w-32 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => {
                          setModelImage(null);
                          setModelImagePreview(null);
                        }}
                        className="inline-flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="text-sm text-gray-600">
                        <label className="cursor-pointer text-purple-600 hover:text-purple-700 font-medium">
                          <span>Upload a file</span>
                          <input
                            type="file"
                            className="sr-only"
                            accept="image/*"
                            onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0], 'model')}
                          />
                        </label>
                        <span> or drag and drop</span>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG up to 2MB</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Product
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProductPage; 
